import { Direction } from "../engine/direction";
import { Strings } from "../engine/strings";
import { createOverlay, createButton, IconLoader } from "./dom";
import { playerSpriteComposer } from "./playerSpriteComposer";
import type { PlayerVisualState } from "./playerVisualState";
import { playUiSound, UiSound } from "./sound/uiSoundPlayer";
import { buildCharacterCards } from "./viewModels/characterSelectViewModel";
import { loadMaxLevelReached } from "./progressStorage";

const PLAYER_SPRITE_CELL_SIZE = 40;
const LOADOUT_ICON_SIZE = 28;

export function showCharacterSelect(onPick: (characterId: string) => void): void {
	const overlay = createOverlay({
		level: 2,
		onEscape: () => overlay.close(),
		onBackdropClick: () => overlay.close(),
	});
	overlay.panel.classList.add("char-select");

	const title = document.createElement("div");
	title.className = "modal-title";
	title.textContent = Strings.get("ui_characterSelectTitle") ?? "Choose your character";
	overlay.panel.appendChild(title);

	const subtitle = document.createElement("div");
	subtitle.className = "char-select-subtitle";
	subtitle.textContent = Strings.get("ui_characterSelectSubtitle") ?? "";
	overlay.panel.appendChild(subtitle);

	const cardsRow = document.createElement("div");
	cardsRow.className = "char-select-cards";
	overlay.panel.appendChild(cardsRow);

	const selectableButtons: HTMLButtonElement[] = [];
	for (const card of buildCharacterCards(loadMaxLevelReached())) {
		const button = document.createElement("button");
		button.className = card.locked ? "char-card locked" : "char-card";
		button.disabled = card.locked;

		const name = document.createElement("div");
		name.className = "char-card-name";
		name.textContent = card.name;
		button.appendChild(name);

		// Wrapper keeps the LOCKED badge outside .char-card-sprite — the locked
		// grayscale filter would otherwise apply to the badge too.
		const spriteWrap = document.createElement("div");
		spriteWrap.className = "char-card-sprite-wrap";
		const sprite = document.createElement("div");
		sprite.className = "char-card-sprite";
		appendPlayerSprite(sprite, card.spriteState);
		spriteWrap.appendChild(sprite);
		if (card.locked) {
			const lockedLabel = document.createElement("div");
			lockedLabel.className = "char-card-locked-label";
			lockedLabel.textContent = Strings.get("ui_lockedLabel") ?? "LOCKED";
			spriteWrap.appendChild(lockedLabel);
		}
		button.appendChild(spriteWrap);

		const loadout = document.createElement("div");
		loadout.className = "char-card-loadout";
		for (const filename of card.loadoutIconFilenames) {
			appendLoadoutIcon(loadout, filename);
		}
		button.appendChild(loadout);

		const description = document.createElement("div");
		description.className = "char-card-desc";
		description.textContent = card.description;
		button.appendChild(description);

		if (card.locked) {
			const hint = document.createElement("div");
			hint.className = "char-card-hint";
			hint.textContent = card.unlockHint ?? "";
			button.appendChild(hint);
		} else {
			button.onclick = () => {
				playUiSound(UiSound.CLICK);
				overlay.close();
				onPick(card.id);
			};
			selectableButtons.push(button);
		}
		cardsRow.appendChild(button);
	}

	overlay.panel.appendChild(createButton(Strings.get("ui_btnBack") ?? "Back", () => overlay.close(), "ghost"));

	overlay.element.addEventListener("keydown", (e) => {
		if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
		e.preventDefault();
		const focusedIndex = selectableButtons.indexOf(document.activeElement as HTMLButtonElement);
		const delta = e.key === "ArrowLeft" ? -1 : 1;
		const next = (focusedIndex + delta + selectableButtons.length) % selectableButtons.length;
		selectableButtons[next]?.focus();
	});
	selectableButtons[0]?.focus();
}

function appendPlayerSprite(parent: HTMLElement, state: PlayerVisualState): void {
	playerSpriteComposer.getAnimation(state, Direction.E, PLAYER_SPRITE_CELL_SIZE, PLAYER_SPRITE_CELL_SIZE).then((animation) => {
		const image = animation.frames[0]?.sprite.image;
		if (!(image instanceof HTMLCanvasElement) || !parent.isConnected) return;
		parent.appendChild(IconLoader.copyIconCanvas(image));
	});
}

function appendLoadoutIcon(parent: HTMLElement, filename: string): void {
	IconLoader.loadIcon(filename, LOADOUT_ICON_SIZE).then((icon) => {
		if (parent.isConnected) parent.appendChild(IconLoader.copyIconCanvas(icon));
	});
}
